import type { BusinessLocation } from '../components/quote-steps/business-location';

type LocationAddress = Pick<
  BusinessLocation,
  'id' | 'cep' | 'street' | 'number' | 'complement'
>;

function normalizeAddressPart(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Chave do endereço completo: CEP + logradouro + número + complemento.
 * O complemento diferencia locais no mesmo prédio (salas, andares, blocos),
 * então dois locais só são duplicados se também tiverem o mesmo complemento.
 */
function buildAddressKey(location: LocationAddress): string {
  return [
    location.cep.replace(/\D/g, ''),
    normalizeAddressPart(location.street),
    normalizeAddressPart(location.number),
    normalizeAddressPart(location.complement),
  ].join('|');
}

/**
 * Retorna os ids dos locais de risco cujo endereço (CEP + logradouro +
 * número + complemento) se repete na lista — todos os envolvidos na
 * duplicidade são retornados, para que a UI destaque cada card a corrigir.
 */
export function findDuplicateBusinessLocationIds(
  locations: LocationAddress[]
): string[] {
  const idsByAddressKey = new Map<string, string[]>();

  for (const location of locations) {
    const key = buildAddressKey(location);
    idsByAddressKey.set(key, [
      ...(idsByAddressKey.get(key) ?? []),
      location.id,
    ]);
  }

  return [...idsByAddressKey.values()]
    .filter(ids => ids.length > 1)
    .flat();
}
